import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Book, Code, Settings, Link2, FileText, Sparkles } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { signInWithGoogle } from "@/utils/auth";

const Documentation = () => {
  const navigate = useNavigate();

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  const docsSections = [
    {
      title: "Getting Started",
      icon: Book,
      description: "Learn how to set up your account, connect your website, and start generating content",
      id: "getting-started"
    },
    {
      title: "Content Generation",
      icon: FileText,
      description: "Create SEO-optimized articles with AI, customize settings, and manage your content",
      id: "content-generation"
    },
    {
      title: "Backlinks",
      icon: Link2,
      description: "How to claim premium backlinks and track your domain authority growth",
      id: "backlinks"
    },
    {
      title: "LLMs.txt Generator",
      icon: Sparkles,
      description: "Generate AI-friendly files to help chatbots and search engines understand your website",
      id: "llms-txt"
    }
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      // Calculate header height dynamically
      const getHeaderHeight = () => {
        const header = document.querySelector('header');
        return header ? header.offsetHeight + 20 : 100; // Add 20px padding
      };
      
      const headerOffset = getHeaderHeight();
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Handle hash links on page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Small delay to ensure page is fully rendered
      setTimeout(() => {
        const id = hash.substring(1);
        scrollToSection(id);
      }, 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Documentation - Complete Guide to Using Seoro | Getting Started, Content Generation, Backlinks"
        description="Complete documentation for Seoro. Learn how to get started, generate SEO-optimized content, claim premium backlinks, generate LLM.txt files, and integrate with Shopify, WordPress, and Webflow."
        keywords="Seoro documentation, SEO platform guide, content generation guide, backlink guide, LLM.txt guide, CMS integration guide, getting started"
      />
      <Header onSignIn={handleSignIn} />
      
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="space-y-12">
          <div className="text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground">
              Documentation
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Everything you need to know about using Seoro effectively.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {docsSections.map((section, index) => {
              const Icon = section.icon;
              return (
                <button
                  key={index}
                  onClick={() => scrollToSection(section.id)}
                  className="bg-muted/30 rounded-xl p-8 hover:bg-muted/50 transition-all hover:shadow-lg group text-left cursor-pointer"
                >
                  <Icon className="h-10 w-10 text-primary mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-bold text-foreground mb-2">{section.title}</h3>
                  <p className="text-muted-foreground">{section.description}</p>
                </button>
              );
            })}
          </div>

          <div className="bg-muted/30 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-foreground mb-6">Integration Guides</h2>
            <p className="text-muted-foreground mb-6">
              Connect Seoro with your CMS platform to publish articles directly. One-click publishing saves you time and keeps your workflow smooth.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a
                href="/docs/shopify-integration"
                className="p-6 bg-background rounded-xl hover:bg-muted/50 transition-all hover:shadow-lg group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                    <img
                      src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/shopify-logo.svg"
                      alt="Shopify"
                      className="h-10 w-auto max-w-[120px] object-contain group-hover:scale-110 transition-transform"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">Shopify Integration</h3>
                    <p className="text-sm text-muted-foreground">Publish articles directly to your Shopify store</p>
                  </div>
                </div>
              </a>
              <a
                href="/docs/wordpress-integration"
                className="p-6 bg-background rounded-xl hover:bg-muted/50 transition-all hover:shadow-lg group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                    <img
                      src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/WordPress_blue_logo.svg"
                      alt="WordPress"
                      className="h-10 w-auto max-w-[120px] object-contain group-hover:scale-110 transition-transform"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">WordPress Integration</h3>
                    <p className="text-sm text-muted-foreground">Publish directly to your WordPress site</p>
                  </div>
                </div>
              </a>
              <a
                href="/docs/webflow-integration"
                className="p-6 bg-background rounded-xl hover:bg-muted/50 transition-all hover:shadow-lg group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                    <img
                      src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/Mark_Logo_Blue.svg"
                      alt="Webflow"
                      className="h-10 w-auto max-w-[120px] object-contain group-hover:scale-110 transition-transform"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">Webflow Integration</h3>
                    <p className="text-sm text-muted-foreground">Integrate with your Webflow projects</p>
                  </div>
                </div>
              </a>
              <div className="p-6 bg-background rounded-xl border-2 border-dashed border-border/50">
                <h3 className="font-semibold text-foreground text-muted-foreground mb-1">More Coming Soon</h3>
                <p className="text-sm text-muted-foreground">We're working on additional integrations</p>
              </div>
            </div>
          </div>

          {/* Getting Started Guide */}
          <div id="getting-started" className="bg-muted/30 rounded-2xl p-8 space-y-6 scroll-mt-24">
            <div className="flex items-center gap-3">
              <Book className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Getting Started</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">1. Sign Up and Connect Your Website</h3>
                <p className="leading-relaxed">
                  Start by signing up with Google. Once you're in, connect your website by entering your domain. 
                  This helps us analyze your niche and create content that matches your business.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">2. Connect Google Search Console</h3>
                <p className="leading-relaxed">
                  Link your Google Search Console account to track real performance data. You'll see actual clicks, 
                  impressions, and rankings—not estimates. This helps you make data-driven decisions.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">3. Set Up Your CMS Integration</h3>
                <p className="leading-relaxed">
                  Connect your CMS platform (Shopify, WordPress, or Webflow) to enable one-click publishing. Framer integration coming soon! 
                  Once connected, you can publish articles directly from Seoro without copying and pasting.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">4. Start Generating Content</h3>
                <p className="leading-relaxed">
                  Go to the "My Content" tab and click "Generate Article". Customize your settings—choose your language, 
                  writing style, word count, and image preferences. Then let our AI create SEO-optimized content for you.
                </p>
              </div>
            </div>
          </div>

          {/* Content Generation Guide */}
          <div id="content-generation" className="bg-muted/30 rounded-2xl p-8 space-y-6 scroll-mt-24">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Content Generation</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Creating Articles</h3>
                <p className="leading-relaxed mb-3">
                  Navigate to the "My Content" tab and click "Generate Article". Our AI will create SEO-optimized content 
                  based on your website's niche and target keywords. Each article includes:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Strategic keyword optimization</li>
                  <li>Natural internal or external links</li>
                  <li>AI-generated images in your preferred style</li>
                  <li>SEO-friendly structure and formatting</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Customizing Your Content</h3>
                <p className="leading-relaxed">
                  Before generating, you can customize:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                  <li><strong className="text-foreground">Language:</strong> Choose from 76 supported languages</li>
                  <li><strong className="text-foreground">Writing Style:</strong> Professional, casual, or technical</li>
                  <li><strong className="text-foreground">Word Count:</strong> Set your preferred article length</li>
                  <li><strong className="text-foreground">Image Style:</strong> Realistic, cartoon, or other styles</li>
                  <li><strong className="text-foreground">Article Format:</strong> Blog post, guide, listicle, or review</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Reviewing and Publishing</h3>
                <p className="leading-relaxed">
                  After generation, review your article. You can edit the content, adjust images, or regenerate if needed. 
                  Once you're happy, publish directly to your connected CMS platform with one click.
                </p>
              </div>
            </div>
          </div>

          {/* Backlinks Guide */}
          <div id="backlinks" className="bg-muted/30 rounded-2xl p-8 space-y-6 scroll-mt-24">
            <div className="flex items-center gap-3">
              <Link2 className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Backlinks</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">How It Works</h3>
                <p className="leading-relaxed">
                  Every month, you receive 10 premium backlinks from high-authority websites (DA 70+). These backlinks 
                  are automatically placed on relevant, high-quality sites that boost your domain authority and search rankings.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Claiming Your Backlinks</h3>
                <p className="leading-relaxed">
                  Go to the "Backlinks" tab in your dashboard. You'll see available backlink opportunities. Click "Claim" 
                  on any backlink you want. Once claimed, we handle the placement automatically—no outreach or negotiation needed.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Tracking Your Backlinks</h3>
                <p className="leading-relaxed">
                  In the Backlinks tab, you can see exactly where your backlinks are placed. View the source domain, 
                  anchor text, and status. Monitor your domain authority growth over time as these high-quality backlinks 
                  boost your rankings.
                </p>
              </div>
            </div>
          </div>

          {/* LLMs.txt Guide */}
          <div id="llms-txt" className="bg-muted/30 rounded-2xl p-8 space-y-6 scroll-mt-24">
            <div className="flex items-center gap-3">
              <Sparkles className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">LLMs.txt Generator</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">What is LLMs.txt?</h3>
                <p className="leading-relaxed">
                  LLMs.txt is a file that helps AI systems understand your website. When someone asks an AI chatbot about 
                  your business, or when search engines try to understand your content, this file gives them a clear, 
                  organized summary of what you do and how you help customers.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Generating Your File</h3>
                <p className="leading-relaxed">
                  Go to the "LLMs.txt" tab in your dashboard. Enter your website URL and click "Generate". Our system 
                  will analyze your website and create a comprehensive LLMs.txt file in seconds.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Adding to Your Website</h3>
                <p className="leading-relaxed">
                  Once generated, download the file and upload it to your website's root directory. The file must be named 
                  exactly "llms.txt" and placed at yoursite.com/llms.txt. We provide step-by-step guides for WordPress, 
                  Shopify, Webflow, and other platforms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer onSignIn={handleSignIn} />
    </div>
  );
};

export default Documentation;


